import React, { useState, useEffect} from "react";
import { FaSearch } from "react-icons/fa";
import { useSearch } from "../../context/SearchContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Medicine.scss";
import { useAuth } from "../../context/AuthContext";
import { useLocation } from 'react-router-dom'; 
import { useSearchToggle } from "../../context/SearchToggleContext";


const MedicineSearch = () => {
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(5);
  const [page, setPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [typing, setTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suppressSuggestions, setSuppressSuggestions] = useState(false);
  const { user } = useAuth();
  const { setSearchResult } = useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSearchVisible } = useSearchToggle();

  useEffect(() => {
    const navSearchResult = location.state?.searchResult;
  
    if (location.pathname === '/medicines' && query.trim() === "" && !navSearchResult) {
      setSearchResult([]); 
      setFiltered([]); 
      setSelectedItem(null); 
      setTyping(false); 
      setShowSuggestions(false); 
    } else if (navSearchResult && navSearchResult.length > 0) {
      setSearchResult(navSearchResult);
    }
  }, [location.pathname, query, setSearchResult]);
  


  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (suppressSuggestions || !typing || !query.trim()) {
        setFiltered([]);
        setError("");
        setShowSuggestions(false);
        return;
      }

      fetchSuggestions();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query, page, typing, suppressSuggestions]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(
        `/medicines?name=${query}&limit=${limit}&skip=${(page - 1) * limit}`,
        { withCredentials: true, validateStatus: () => true }
      );

      if (res.status === 200 && res.data.length > 0) {
        const nameResults = res.data.map((med) => ({ ...med, _type: "name" }));
        setFiltered(nameResults);
        setError("");
      } else {
        const catRes = await axios.get(
          `/medicines?category=${query}&limit=${limit}&skip=${(page - 1) * limit}`,
          { withCredentials: true, validateStatus: () => true }
        );

        if (catRes.status === 200 && catRes.data.length > 0) {
          const categoryResults = catRes.data.map((med) => ({
            ...med,
            _type: "category",
            _category: med.category,
          }));

          setFiltered([
            ...categoryResults,
            {
              _id: "category-view-all",
              name: `View all medicines in "${query}"`,
              category: query,
              isCategoryViewAll: true,
              _type: "view-all",
            },
          ]);
          setError("");
        } else {
          setFiltered([]);
          setError("No medicines found.");
        }
      }

      setShowSuggestions(true);
    } catch (err) {
      console.error("Suggestion error:", err);
      setFiltered([]);
      setError("Error while fetching suggestions.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    const searchText = query.trim();
    if (!searchText) return;

    setLoading(true);
    setError("");
    setFiltered([]);
    setSuppressSuggestions(true);

    try {
      if (selectedItem) {
        if (selectedItem._type === "view-all") {
          const response = await axios.get(`/medicines?category=${selectedItem.category}`, {
            withCredentials: true,
            validateStatus: () => true,
          });

          if (response.status === 200) {
            setSearchResult(response.data);
            setFiltered([]);
            setError("");
          } else {
            setSearchResult([]);
          }
        } else {
          setSearchResult([selectedItem]);
        }
        setSelectedItem(null);
        setTyping(false);
        setShowSuggestions(false);
        navigate("/medicines" ,{ state: { searchResult: response.data } });
        return;
      }

      let response = await axios.get(`/medicines?name=${searchText}`, {
        withCredentials: true,
        validateStatus: () => true,
      });

      if (!(response.status === 200 && response.data.length > 0)) {
        response = await axios.get(`/medicines?category=${searchText}`, {
          withCredentials: true,
          validateStatus: () => true,
        });
      }

      if (response.status === 200 && response.data.length > 0) {
        setSearchResult(response.data);
        setFiltered([]);
        setError("");
        navigate("/medicines", { state: { searchResult: response.data } });
      } else {
        setSearchResult([]);
        setError("No matching medicine or category found.");
      }
    } catch (err) {
      console.error("Search error:", err);
      setError("Search failed. Try again.");
    } finally {
      setLoading(false);
      setTyping(false);
      setShowSuggestions(false);
      setSuppressSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setFocusedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
    } else if (e.key === "Enter") {
      if (focusedIndex >= 0 && filtered[focusedIndex]) {
        handleSelect(filtered[focusedIndex]);
      } else {
        handleSearch();
      }
    }
  };

  const handleSelect = (med) => {
    if (med.isCategoryViewAll) {
      setSelectedItem(med);
      setQuery(med.category);
    } else {
      setSelectedItem(med);
      setQuery(med.name);
    }
    setFiltered([]);
    setShowSuggestions(false);
    setTyping(false);
  };

  const handleSeeMore = () => {
    setPage((prev) => prev + 1);
  };

  return (
    <div className="search-bar">
      <input
        type="text"
        disabled={!user}
        placeholder="Search medicine or category..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setPage(1);
          setSelectedItem(null);
          setError("");
          setTyping(true);
        }}
        onKeyDown={handleKeyDown}
      />
      <button className="search-btn" onClick={handleSearch}>
        <FaSearch style={{ color: "#ffffff" }} />
      </button>

      {query && showSuggestions && (
        <ul className="suggestions">
          {loading ? (
            <li>Loading...</li>
          ) : error ? (
            <li className="error">{error}</li>
          ) : filtered.length > 0 ? (
            filtered.map((med, idx) => {
              const displayText =
                med._type === "view-all"
                  ? med.name
                  : `${med.name} (${med.category})`;

              const lowerText = displayText.toLowerCase();
              const input = query.toLowerCase();
              const matchIndex = lowerText.indexOf(input);

              let highlighted;
              if (matchIndex !== -1) {
                const before = displayText.slice(0, matchIndex);
                const match = displayText.slice(matchIndex, matchIndex + input.length);
                const after = displayText.slice(matchIndex + input.length);
                highlighted = (
                  <>
                    {before}
                    <span className="highlight">{match}</span>
                    {after}
                  </>
                );
              } else {
                highlighted = displayText;
              }

              return (
                <li
                  key={med._id}
                  className={focusedIndex === idx ? "focused" : ""}
                  onClick={() => handleSelect(med)}
                >
                  {highlighted}
                </li>
              );
            })
          ) : (
            <li className="no-suggestions">No suggestions found.</li>
          )}

          {filtered.length === limit && !loading && (
            <li className="see-more-btn" onClick={handleSeeMore}>
              See more
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default MedicineSearch;
